import { useAppContext } from '../AppContext/AppContext';
import { AppRoute, ROUTE_FLOW, skippableRoutes } from '../constants';
import { SelfConsumptionPlusModal } from '../Pages/SelfConsumptionPlus/SelfConsumptionPlusModal/SelfConsumptionPlusModal';
import { OrderFlow } from '../types';
import { useCurrentRoute } from './useCurrentRoute';
import { useCurrentUserFeatureFlags } from './useCurrentUserFeatureFlags';
import { useTopScrollNavigate } from './useTopScrollNavigate';

const getSurroundingRoutes = (
  currentRoute: AppRoute,
  flow: OrderFlow | null,
): {
  next?: string;
  previous?: string;
} => {
  const routeFlow = ROUTE_FLOW[flow || OrderFlow.energyDynamic];
  const currentRouteIndex = routeFlow.indexOf(currentRoute);
  if (currentRouteIndex === -1) return {};
  return {
    next: currentRouteIndex < routeFlow.length - 1 ? routeFlow[currentRouteIndex + 1] : undefined,
    previous: currentRouteIndex > 0 ? routeFlow[currentRouteIndex - 1] : undefined,
  };
};

type UseOrderFlowNavigationProps = {
  navigate: (path: string) => void;
  navigateNext?: () => void;
  navigatePrevious?: () => void;
  next?: string;
  previous?: string;
};

export const useOrderFlowNavigation = (): UseOrderFlowNavigationProps => {
  const currentRoute = useCurrentRoute();
  const navigate = useTopScrollNavigate();
  const { orderFlow, currentModal, setCurrentModal, user } = useAppContext();
  const { allowToAccessEnergyStore } = useCurrentUserFeatureFlags();

  const skippableRoute = skippableRoutes.has(currentRoute);

  const { next, previous } = getSurroundingRoutes(currentRoute, orderFlow);

  const returns: UseOrderFlowNavigationProps = {
    next,
    previous,
    navigate,
  };

  if (previous) {
    if (user && previous === AppRoute.ROOT) {
      if (allowToAccessEnergyStore)
        returns.navigatePrevious = () => navigate(AppRoute.ENERGY_STORE);
    } else {
      returns.navigatePrevious = () => {
        if (!user && currentRoute === AppRoute.TARIFFS_COMPARISON) navigate(AppRoute.ROOT);
        else navigate(previous);
      };
    }
  }

  if (currentRoute === AppRoute.SELF_CONSUMPTION_PLUS) {
    if (currentModal && next) {
      returns.navigateNext = () => navigate(next);
    } else {
      returns.navigateNext = () => setCurrentModal(<SelfConsumptionPlusModal />);
    }
  } else if (next && skippableRoute) {
    returns.navigateNext = () => navigate(next);
  }

  return returns;
};
